import { useEffect, useMemo, useRef, useState } from "react";
import type { ApiStock } from "../services/api";
import { useLivePrice } from "../hooks/useLivePrice";
import { fmtNum, fmtPercent, percentClass } from "../services/format";

interface Props {
  open: boolean;
  stock: ApiStock | null;
  onClose: () => void;
  onConfirm: (price: number, amount: number) => Promise<void> | void;
}

// 加仓对话框：计算加仓后的新成本价、总持股数及本次加仓数量
export function AddPositionDialog({ open, stock, onClose, onConfirm }: Props) {
  const [buyPrice, setBuyPrice] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const manualEdited = useRef(false);
  const live = useLivePrice(stock ? `${stock.exchange}${stock.code}` : undefined, open && !!stock);

  // 打开对话框时重置手动编辑标记
  useEffect(() => {
    if (open) manualEdited.current = false;
  }, [open]);

  // 未手动编辑时持续跟随当日最新价自动填入
  useEffect(() => {
    if (open && stock && !manualEdited.current && live.price != null) {
      setBuyPrice(String(live.price));
    }
  }, [open, stock, live.price]);

  const result = useMemo(() => {
    const p = Number(buyPrice);
    const amt = Number(amount);
    const qty = stock?.quantity ?? 0;
    const cost = stock?.costPrice ?? 0;
    if (!p || p <= 0 || !amt || amt <= 0 || qty <= 0) return null;
    const addQty = Math.floor(amt / p);
    if (addQty <= 0) return null;
    const newQty = qty + addQty;
    const newCost = Math.round(((qty * cost + amt) / newQty) * 100) / 100;
    return { addQty, newQty, newCost };
  }, [buyPrice, amount, stock]);

  if (!open || !stock) return null;

  const submit = async () => {
    const p = Number(buyPrice);
    const amt = Number(amount);
    if (!p || p <= 0 || !amt || amt <= 0) return;
    setBusy(true);
    try {
      await onConfirm(p, amt);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const percentClassOf = percentClass(live.percent);

  return (
    <div className="dialog-overlay" onMouseDown={onClose}>
      <div className="dialog" onMouseDown={(e) => e.stopPropagation()}>
        <div className="dialog-title">
          {stock.name}（{stock.exchange}
          {stock.code}）
        </div>

        <div className="flex justify-between mb-3 text-13px">
          <span className="flat">
            当前持仓 {fmtNum(stock.quantity)} 股 / 成本 {fmtNum(stock.costPrice)}
          </span>
        </div>
        <div className="flex justify-between mb-4 text-13px">
          <span className="flat">当日价格</span>
          <span>
            <span className="font-semibold">{fmtNum(live.price)}</span>{" "}
            <span className={percentClassOf}>{fmtPercent(live.percent == null ? null : live.percent)}</span>
          </span>
        </div>

        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <label className="label" style={{ marginBottom: 0 }}>买入价（元）</label>
            {manualEdited.current && live.price != null && Number(buyPrice) !== live.price && (
              <button
                type="button"
                className="text-12px"
                style={{ color: "var(--accent)" }}
                onClick={() => {
                  manualEdited.current = false;
                  setBuyPrice(String(live.price));
                }}
              >
                ↺ 恢复最新价
              </button>
            )}
          </div>
          <input
            className="input"
            type="number"
            min={0.01}
            step="0.01"
            inputMode="decimal"
            value={buyPrice}
            onChange={(e) => {
              manualEdited.current = true;
              setBuyPrice(e.target.value);
            }}
          />
        </div>
        <div className="mb-3">
          <label className="label">加仓金额（元）</label>
          <input
            className="input"
            type="number"
            min={0.01}
            step="0.01"
            inputMode="decimal"
            placeholder="请输入加仓金额"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>

        {result && (
          <div className="mb-4 p-3 rounded-lg" style={{ background: "var(--hover)" }}>
            <div className="flex justify-between text-13px">
              <span className="flat">本次加仓数量</span>
              <span className="font-semibold">{result.addQty} 股</span>
            </div>
            <div className="flex justify-between text-13px mt-1">
              <span className="flat">加仓后总持股数</span>
              <span className="font-semibold">{result.newQty} 股</span>
            </div>
            <div className="flex justify-between text-13px mt-1">
              <span className="flat">加仓后成本价</span>
              <span className="font-semibold">{fmtNum(result.newCost)} 元</span>
            </div>
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button className="btn" onClick={onClose}>
            取消
          </button>
          <button className="btn btn-primary" disabled={busy || !result} onClick={submit}>
            确认加仓
          </button>
        </div>
      </div>
    </div>
  );
}
